import { useState, useRef, useCallback, useEffect } from 'react';
import { DIFF_TIME, DIFF_POINTS, buildPrompt } from '../data/constants';

const API_URL = 'https://api.anthropic.com/v1/messages';
const API_KEY = process.env.REACT_APP_ANTHROPIC || '';

// ── Cross-session seen-question store (localStorage) ──────────────────────────
const SEEN_MAX = 300;

function loadSeenQuestions(civKey) {
  try {
    const raw = localStorage.getItem(`kabaak_seen_${civKey}`);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveSeenQuestion(civKey, questionText) {
  try {
    const existing = loadSeenQuestions(civKey);
    if (existing.includes(questionText)) return;
    const updated = [...existing, questionText];
    if (updated.length > SEEN_MAX) updated.splice(0, updated.length - SEEN_MAX);
    localStorage.setItem(`kabaak_seen_${civKey}`, JSON.stringify(updated));
  } catch {}
}
// ─────────────────────────────────────────────────────────────────────────────

async function fetchOneFromClaude(civKey, diffKey, recentQuestions) {
  const res = await fetch(API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: 'claude-haiku-4-5-20251001',
      max_tokens: 700,
      messages: [{ role: 'user', content: buildPrompt(civKey, diffKey, recentQuestions) }],
    }),
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error.message);
  const text = data.content[0].text;
  const match = text.match(/\{[\s\S]*\}/);
  if (!match) throw new Error('Invalid response format');
  const q = JSON.parse(match[0]);
  const correctText = q.options[q.correct];
  const shuffled = [...q.options].sort(() => Math.random() - 0.5);
  q.options = shuffled;
  q.correct = shuffled.indexOf(correctText);
  return q;
}

const PREFETCH_COUNT = 3;

export function useQuiz(civKey, diffKey, userId, onFinish) {
  const [qNum,       setQNum]      = useState(1);
  const [score,      setScore]     = useState(0);
  const [streak,     setStreak]    = useState(0);
  const [bestStreak, setBest]      = useState(0);
  const [timeLeft,   setTime]      = useState(DIFF_TIME[diffKey]);
  const [loading,    setLoading]   = useState(true);
  const [curQ,       setCurQ]      = useState(null);
  const [locked,     setLocked]    = useState(false);
  const [picked,     setPicked]    = useState(null);
  const [error,      setError]     = useState(null);

  const timerRef    = useRef(null);
  const lockedRef   = useRef(false);
  const poolRef     = useRef([]);   // ready questions
  const pendingRef  = useRef([]);   // in-flight fetch promises
  // seenTexts = session + cross-session history; loaded from localStorage on mount
  const seenTexts   = useRef(loadSeenQuestions(civKey));
  const stateRef    = useRef({ score: 0, streak: 0, best: 0, answers: [], timings: [] });

  const clearTimer = () => clearInterval(timerRef.current);

  const startTimer = useCallback((q) => {
    const total = DIFF_TIME[diffKey];
    setTime(total);
    clearTimer();
    let remaining = total;
    timerRef.current = setInterval(() => {
      remaining -= 1;
      setTime(remaining);
      if (remaining <= 0) {
        clearTimer();
        if (!lockedRef.current) handleTimeout(q);
      }
    }, 1000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diffKey]);

  function launchFetch() {
    const p = fetchOneFromClaude(civKey, diffKey, seenTexts.current.slice(-20));
    pendingRef.current.push(p);
    p.then(q => {
      pendingRef.current = pendingRef.current.filter(x => x !== p);
      // Record immediately so parallel fetches diverge from each other
      seenTexts.current.push(q.question);
      saveSeenQuestion(civKey, q.question);
      poolRef.current.push(q);
    }).catch(() => {
      pendingRef.current = pendingRef.current.filter(x => x !== p);
    });
    return p;
  }

  // Load initial pool on mount
  useEffect(() => {
    let cancelled = false;
    if (!API_KEY) {
      setLoading(false);
      setError('Add REACT_APP_ANTHROPIC to your environment to play.');
      return;
    }

    // Fire PREFETCH_COUNT fetches in parallel; use the first one to start immediately
    const fetches = Array.from({ length: PREFETCH_COUNT }, () =>
      fetchOneFromClaude(civKey, diffKey, seenTexts.current.slice(-20))
    );

    fetches[0].then(q => {
      if (cancelled) return;
      seenTexts.current.push(q.question);
      saveSeenQuestion(civKey, q.question);
      setCurQ(q);
      setLoading(false);
      startTimer(q);
    }).catch(e => {
      if (!cancelled) { setLoading(false); setError(e.message || 'Could not load question.'); }
    });

    // Buffer remaining fetches into the pool
    fetches.slice(1).forEach(p =>
      p.then(q => {
        if (!cancelled) {
          seenTexts.current.push(q.question);
          saveSeenQuestion(civKey, q.question);
          poolRef.current.push(q);
        }
      }).catch(() => {})
    );

    return () => {
      cancelled = true;
      clearTimer();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadNextQuestion = useCallback(async () => {
    setLoading(true);
    setLocked(false);
    lockedRef.current = false;
    setPicked(null);
    setCurQ(null);
    clearTimer();

    let q = poolRef.current.shift();

    if (!q) {
      // Pool empty — wait for any pending fetch, or fire one now
      const inFlight = pendingRef.current[0] || launchFetch();
      try { q = await inFlight; } catch (e) {
        setLoading(false);
        setError(e.message || 'Could not load question.');
        return;
      }
      // Remove from pool if launchFetch put it there before we grabbed it
      poolRef.current = poolRef.current.filter(x => x !== q);
    }

    setCurQ(q);
    setLoading(false);
    startTimer(q);

    // Keep pool topped up
    const total = poolRef.current.length + pendingRef.current.length;
    if (total < PREFETCH_COUNT - 1) launchFetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [civKey, diffKey]);

  function handleTimeout(q) { handleAnswer(-1, q); }

  function handleAnswer(idx, questionOverride) {
    if (lockedRef.current) return;
    lockedRef.current = true;
    clearTimer();
    setLocked(true);
    setPicked(idx);

    const q      = questionOverride || curQ;
    const st     = stateRef.current;
    const total  = DIFF_TIME[diffKey];
    const ok     = idx !== -1 && idx === q?.correct;
    const timeTaken = total - (timeLeft || 0);

    st.answers.push({
      correct: ok, q: q?.question, options: q?.options,
      correctIndex: q?.correct, pickedIndex: idx,
      correctAnswer: q?.options?.[q?.correct],
    });
    st.timings.push(timeTaken);

    if (ok) {
      st.streak++;
      if (st.streak > st.best) { st.best = st.streak; setBest(st.best); }
      const bonus = Math.max(0, total - timeTaken);
      st.score += (DIFF_POINTS[diffKey] || 10) + Math.round(bonus * 1.5);
      setScore(st.score);
      setStreak(st.streak);
    } else {
      st.streak = 0;
      setStreak(0);
    }

    const nextQNum = qNum + 1;

    // Start fetching next question in background immediately
    launchFetch();

    setTimeout(async () => {
      if (nextQNum > 10) {
        clearTimer();
        onFinish?.({ score: st.score, answers: st.answers, bestStreak: st.best, timings: st.timings });
      } else {
        setQNum(nextQNum);
        await loadNextQuestion();
      }
    }, 2200);
  }

  const timerPct   = timeLeft / DIFF_TIME[diffKey];
  const timerColor = timerPct > 0.5 ? '#00D4A8' : timerPct > 0.25 ? '#FF9900' : '#FF5A3C';

  return {
    qNum, score, streak, bestStreak,
    timeLeft, timerPct, timerColor,
    loading, curQ, locked, picked, error,
    source: 'ai',
    pickAnswer: (idx) => handleAnswer(idx),
  };
}
